/**
 * Runtime shape guards for decrypted payloads (desktop slice 4, R4-c).
 *
 * WHY THESE LIVE HERE NOW. The server routes opaque ciphertext, so every surface decrypts
 * arbitrary JSON and has to decide for itself whether the result is the payload it claims to
 * be. Each one wrote its own guard: `isClipboardTextPayload` existed **three times**
 * (`apps/extension/src/shared/clipboard.ts`, `apps/web/src/services/clipboard.ts`,
 * `apps/cli/src/clipboard/core.ts`) and they did not agree. The old sites re-export from here
 * so no call site changes shape.
 *
 * THE THREE COPIES DISAGREED IN THREE WAYS, all resolved below and all worth recording,
 * because each one is the kind of thing that reappears the moment a fourth surface is written:
 *
 * 1. **`sensitive` was validated by web only.** Extension and CLI accepted a payload whose
 *    `sensitive` was present but not a boolean. That matters: `sensitive` drives masking, and
 *    `client-ui`'s `ClipCard.tsx` computes `maskable = sensitive === true`, so a string
 *    `"true"` is NOT sensitive there — it renders in the clear. The strict check wins here.
 *
 *    The extension's omission was deliberate and carried a comment saying `sensitive` is
 *    "handled by the caller's rendering logic, not the payload guard". **That justification is
 *    contradicted by the renderer it defers to** — `=== true` treats a malformed value as
 *    not-sensitive rather than as sensitive, and `clipStore.ts` passes the raw value through
 *    without coercing it. The guard was the only place that could have caught it. Deleted
 *    rather than carried across, because a wrong reason is worse than no reason: it tells the
 *    next reader the gap is covered somewhere else.
 *
 * 2. **`SURFACE_KINDS` was derived exhaustively in two places and hardcoded in the third.**
 *    Extension and CLI built it from a `Record<SurfaceKind, true>`, so adding a surface to the
 *    union is a compile error until they are updated. Web listed the five members as string
 *    literals — complete today, and silently stale the day a sixth arrives, at which point web
 *    would reject clips the other two accept. The exhaustive form wins.
 *
 * 3. **`isClipboardImagePayload` does not exist**, despite R4-c naming it as one of three
 *    guards to move. `ClipboardImagePayload` is a declared wire type with no runtime guard on
 *    any surface — the image slice shipped the type, not the consumer. Nothing is moved for it
 *    here; it lands with the code that first needs it.
 *
 * SECURITY: never log a payload's `content`, `title` or `body` from a guard, or from anything
 * that calls one. See `.claude/rules/security.md`.
 */

import type { ClipboardTextPayload, NotificationPayload, SurfaceKind } from "./types";

/**
 * Exhaustiveness anchor: a missing member is a COMPILE error, so extending `SurfaceKind`
 * cannot silently leave a guard rejecting the new surface. This is the form two of the three
 * old copies used; the third hardcoded the list and would have gone stale.
 */
const EXHAUSTIVE_SURFACE_KINDS = {
  phone: true,
  extension: true,
  pwa: true,
  cli: true,
  desktop: true,
} satisfies Record<SurfaceKind, true>;

/**
 * Typed `ReadonlySet<string>`, NOT `ReadonlySet<SurfaceKind>`, and that is deliberate.
 *
 * A `Set<SurfaceKind>` cannot be `.has()`-ed with an unvalidated string, so both of the old
 * copies wrote `SURFACE_KINDS.has(o.surface as SurfaceKind)` — asserting to the compiler that
 * the value IS a `SurfaceKind` on the very line that checks whether it is one. The cast could
 * never fail loudly; it just made the type system agree with an unchecked claim.
 *
 * Keeping the set's element type wide moves the narrowing to where it belongs: the runtime
 * check succeeds, and the guard's `value is ClipboardTextPayload` return type is what carries
 * the conclusion to callers. Exhaustiveness is not lost — `EXHAUSTIVE_SURFACE_KINDS` above
 * still makes a new `SurfaceKind` a compile error.
 */
const SURFACE_KINDS: ReadonlySet<string> = new Set(Object.keys(EXHAUSTIVE_SURFACE_KINDS));

/** Same reasoning as `SURFACE_KINDS` — wide element type, no cast at the call site. */
const PROVENANCES: ReadonlySet<string> = new Set(["device", "cloud"]);

/**
 * True when `value` is a well-formed `ClipboardTextPayload`.
 *
 * The image variant of the `ClipboardPayload` union is rejected — deferred, not malformed.
 */
export function isClipboardTextPayload(value: unknown): value is ClipboardTextPayload {
  if (value == null || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;

  if (v.type !== "clipboard") return false;
  if (v.contentType !== "text") return false;
  if (typeof v.content !== "string") return false;
  if (typeof v.timestamp !== "number") return false;
  // Optional, but if present it must be a real boolean — see note 1 in the module header.
  if (v.sensitive !== undefined && typeof v.sensitive !== "boolean") return false;
  if (typeof v.provenance !== "string" || !PROVENANCES.has(v.provenance)) return false;

  const origin = v.origin;
  if (origin == null || typeof origin !== "object") return false;
  const o = origin as Record<string, unknown>;
  if (typeof o.label !== "string") return false;
  if (typeof o.surface !== "string" || !SURFACE_KINDS.has(o.surface)) return false;

  return true;
}

/**
 * True when `value` is a well-formed `NotificationPayload`.
 *
 * Without this a malformed plaintext — an older Android client, a future payload variant —
 * passes an unsafe `as` cast and emits `undefined` fields downstream. Reject loudly instead.
 */
export function isNotificationPayload(value: unknown): value is NotificationPayload {
  if (value == null || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    v.type === "notification" &&
    typeof v.app === "string" &&
    typeof v.appPackage === "string" &&
    typeof v.title === "string" &&
    typeof v.body === "string" &&
    typeof v.timestamp === "number"
  );
}
